import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { usePatchDataSourceFields } from 'api/services/dataSourceService';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { DarwinFlex } from 'components/darwin-ui';
import { DataItem } from 'components/ui/dataTables/CategoriesCollapse';
import styles from './index.module.css';
import { DataSourceFieldsList } from 'components/ui/dataSources/DataSourceFieldsList';
import { TemplatesCollapse } from 'components/ui/dataSources/TemplatesCollapse';
import { ROUTES } from 'routes/routes';

export const DataSourceFields = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedKey, setSelectedKey] = useState<DataItem | null>(null);
  const [mappedValues, setMappedValues] = useState<Record<string, string>>({});
  const { openNotification } = useFloatNotification();
  const patchDataSourceFields = usePatchDataSourceFields();
  const { id: dataSourceId } = useParams();
  const queryClient = useQueryClient();

  const handleTemplateSelect = useCallback((item: DataItem | null) => {
    setSelectedKey(item);
    setMappedValues({});
  }, []);

  const handleSave = useCallback(async () => {
    if (!dataSourceId) return;
    try {
      const payload = {
        fields: Object.entries(mappedValues).map(([id, sourceColumnName]) => ({
          id,
          sourceColumnName,
        })),
      };
      await patchDataSourceFields(dataSourceId, payload);
      openNotification(
        t('success'),
        t('data-sources.fields-view.save-success'),
        'success'
      );
      setMappedValues({});
      queryClient.invalidateQueries({ queryKey: ['data-source-fields'] });
    } catch (error) {
      openNotification(
        t('error'),
        t('data-sources.fields-view.save-error'),
        'error'
      );
    }
  }, [
    dataSourceId,
    mappedValues,
    patchDataSourceFields,
    openNotification,
    t,
    queryClient,
  ]);

  return (
    <DarwinFlex vertical>
      <ContentHeader
        title={t('data-sources.title')}
        onSave={handleSave}
        onCancel={() => navigate(ROUTES.DATA_SOURCES.LIST)}
        saveDisabled={Object.keys(mappedValues).length === 0}
      />

      <DarwinFlex className={styles.contentContainer}>
        <TemplatesCollapse
          selectedKey={selectedKey || undefined}
          onSelect={handleTemplateSelect}
        />

        <DataSourceFieldsList
          templateId={selectedKey?.key || ''}
          templateName={selectedKey?.label || ''}
          mappedValues={mappedValues}
          onMappedChange={(fieldId, value) => {
            setMappedValues(prev => ({ ...prev, [fieldId]: value }));
          }}
        />
      </DarwinFlex>
    </DarwinFlex>
  );
};
